/**
 * iter63w — Site Admin Cleanup Candidates card.
 *
 * Surfaces stale/junk leagues that meet ALL of:
 *   - created > N days ago (default 30)
 *   - no draft started
 *   - no matches played
 *   - ≤1 participant (admin-only)
 *   - no archive_status
 *
 * Lets the site admin multi-select and bulk-delete. Reuses the existing
 * `siteBulkDeleteLeagues` endpoint, so the cascade (tournaments, matches,
 * scheduled_matches, participants, drafts, snapshots) is unchanged.
 */
import { useEffect, useState, useCallback } from "react";
import { Trash2, RefreshCw, AlertTriangle, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DAY_PRESETS = [
    { days: 30, label: "30+ days" },
    { days: 14, label: "14+ days" },
    { days: 7, label: "7+ days" },
    { days: 1, label: "1+ day" },
    { days: 0, label: "Any age" },
];

export default function SiteAdminCleanupCard({ onChanged }) {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.siteLeaguesCleanupCandidates(days);
            setData(d);
            setSelected(new Set());
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cleanup query failed");
        }
        setLoading(false);
    }, [days]);

    useEffect(() => { load(); }, [load]);

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const allSelected = data && data.candidates.length > 0 && selected.size === data.candidates.length;
    const toggleAll = () => {
        if (allSelected) setSelected(new Set());
        else setSelected(new Set(data.candidates.map(c => c.league_id)));
    };

    const bulkDelete = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        if (!window.confirm(`PERMANENTLY delete ${ids.length} stale league(s) and all their tournaments, matches, participants, and snapshots? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            const res = await api.siteBulkDeleteLeagues(ids);
            toast.success(`Deleted ${res.deleted_count} league(s)`);
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk delete failed");
        }
        setDeleting(false);
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="site-cleanup-card">
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-blaze" /> Stale league cleanup
                    </h3>
                    <p className="text-xs text-zinc-400 mt-1">
                        Surfaces leagues with no draft, no matches, and ≤1 participant —
                        safe to permanently delete to keep the DB tidy.
                    </p>
                </div>
                <button
                    data-testid="cleanup-refresh-btn"
                    onClick={load}
                    disabled={loading}
                    className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="flex flex-wrap gap-1.5" data-testid="cleanup-age-chips">
                {DAY_PRESETS.map(p => (
                    <button
                        key={p.days}
                        data-testid={`cleanup-age-${p.days}-btn`}
                        onClick={() => setDays(p.days)}
                        className={`pill border-2 text-[10px] font-bold uppercase py-1 px-2 transition ${
                            days === p.days
                                ? "bg-blaze text-black border-blaze"
                                : "bg-transparent text-zinc-400 border-[#333] hover:border-[#555]"
                        }`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {data && (
                <>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-xs text-zinc-500" data-testid="cleanup-count">
                            <span className="text-cyber font-bold">{data.candidate_count}</span> stale league(s) found
                        </p>
                        {data.candidates.length > 0 && (
                            <div className="flex gap-2">
                                <button
                                    data-testid="cleanup-select-all-btn"
                                    onClick={toggleAll}
                                    className="brutal-btn-ghost text-xs py-2"
                                >
                                    {allSelected ? <><CheckSquare className="w-3.5 h-3.5 text-cyber" /> Deselect all</> : <><Square className="w-3.5 h-3.5" /> Select all</>}
                                </button>
                                <button
                                    data-testid="cleanup-bulk-delete-btn"
                                    onClick={bulkDelete}
                                    disabled={!selected.size || deleting}
                                    className="brutal-btn text-xs py-2 bg-heat text-white disabled:opacity-60"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete {selected.size || ""}
                                </button>
                            </div>
                        )}
                    </div>

                    {data.candidates.length === 0 ? (
                        <p className="text-xs text-zinc-500 italic py-4 text-center" data-testid="cleanup-empty">
                            No stale leagues at this threshold. Nice and tidy.
                        </p>
                    ) : (
                        <div className="space-y-1.5 max-h-[400px] overflow-y-auto" data-testid="cleanup-list">
                            {data.candidates.map(c => (
                                <label
                                    key={c.league_id}
                                    data-testid={`cleanup-row-${c.league_id}`}
                                    className={`flex items-center gap-2 p-2 border-2 rounded cursor-pointer transition ${
                                        selected.has(c.league_id) ? "bg-blaze/10 border-blaze/50" : "bg-ink border-[#222] hover:border-[#444]"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(c.league_id)}
                                        onChange={() => toggle(c.league_id)}
                                        className="w-3.5 h-3.5"
                                    />
                                    <div className="flex-1 min-w-0 text-xs">
                                        <p className="font-bold truncate text-white">{c.name}</p>
                                        <p className="text-[10px] text-zinc-500">
                                            age {c.age_days}d · participants {c.participant_count} ·
                                            tournaments {c.tournament_count}
                                            {c.tournaments?.[0]?.template ? (
                                                <span> · {c.tournaments[0].template}</span>
                                            ) : null}
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
